/**
 * Constants and types for the Store Status Drawer and Store Onboarding Status
 */
import { UrbanPiperWebhook } from "models/store";

export type StepKey =
  | "Brand Details"
  | "Store Details"
  | "Add Catalog Items"
  | "Add Charges"
  | "Add Taxes"
  | "Urban Piper";

export interface StepDefinition {
  key: StepKey;
  label: string;
}

export const STORE_STATUS_STEP_KEYS = {
  BRAND_DETAILS: "Brand Details",
  STORE_DETAILS: "Store Details",
  CATALOG_ITEMS: "Add Catalog Items",
  CHARGES: "Add Charges",
  TAXES: "Add Taxes",
  URBAN_PIPER: "Urban Piper",
} as const;

export const STORE_STATUS_STEPS: StepDefinition[] = [
  { key: STORE_STATUS_STEP_KEYS.BRAND_DETAILS, label: "Brand" },
  { key: STORE_STATUS_STEP_KEYS.STORE_DETAILS, label: "Store" },
  { key: STORE_STATUS_STEP_KEYS.CATALOG_ITEMS, label: "Catalog" },
  { key: STORE_STATUS_STEP_KEYS.CHARGES, label: "Charges" },
  { key: STORE_STATUS_STEP_KEYS.TAXES, label: "Taxes" },
  { key: STORE_STATUS_STEP_KEYS.URBAN_PIPER, label: "Sync" },
];

export const STORE_STATUS_DATE_FORMATS = {
  DATE_SLASH: "YYYY/MM/DD",
  FULL_DATE_TIME: "LLLL",
  TIME_12H: "hh:mm A",
  TIME_24H: "HH:mm:ss",
  DATE_DISPLAY: "DD MMM YYYY",
} as const;

export const INVOICE_DUE_SOON_DAYS_THRESHOLD = 7;

export const INTEGRATION_PARTY_LABELS: Record<string, string> = {
  UP: "Urban Piper",
  GT: "Grubtech",
};

export const getIntegrationPartyName = (party?: string): string => {
  if (!party) return "N/A";
  return INTEGRATION_PARTY_LABELS[party] || party;
};

export const STORE_STATUS_STORAGE_KEYS = {
  BRAND_REGION: "brand_region",
  CURRENCY: "currency",
} as const;

export const URBAN_PIPER_STATUS_VALUES = {
  SUCCESS: "success",
} as const;

export const STORE_STATUS_MESSAGES = {
  CATALOG: {
    TITLE: "Catalog Inventory Onboarding",
    SUCCESS_DESC:
      "Fantastic! Your store catalog items have been uploaded to our servers and synced successfully.",
    ERROR_DESC:
      "Attention: Store catalog inventory is empty or has not been uploaded yet. Please upload items.",
    BADGE_LABEL: "Current Upload Status",
    SUCCESS_BADGE: "COMPLETED",
    ERROR_BADGE: "PENDING",
  },
  URBAN_PIPER: {
    TITLE: "Urban Piper Gateway Sync",
    SUCCESS_DESC:
      "Great! The Urban Piper API handshake is authenticated and synchronization is fully active.",
    ERROR_DESC:
      "Warning: Urban Piper Gateway credentials have not been connected or synchronized successfully.",
    BADGE_LABEL: "API Sync Status",
    SUCCESS_BADGE: "ACTIVE",
    ERROR_BADGE: "DISCONNECTED",
  },
} as const;

export const formatPhoneNumber = (
  phone: string | string[] | undefined
): string => {
  if (!phone) return "N/A";
  if (Array.isArray(phone)) {
    const valid = phone.filter(Boolean);
    return valid.length > 0 ? valid.join(", ") : "N/A";
  }
  return String(phone);
};

export const formatCurrencyAmount = (
  amount?: number | string,
  currency = "AED"
): string => {
  const num = typeof amount === "number" ? amount : Number(amount) || 0;
  return `${currency} ${num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const formatPlatforms = (
  platforms?: Array<string | { name?: string }>
): string => {
  if (!platforms || !Array.isArray(platforms) || platforms.length === 0) {
    return "None";
  }
  return platforms
    .map((p) => (typeof p === "string" ? p : p?.name))
    .filter(Boolean)
    .join(", ");
};

export const URBAN_PIPER_EVENT_TYPE_OPTIONS = [
  { value: "order_placed", label: "Order Placed (order_placed)" },
  { value: "order_status_update", label: "Order Status Update (order_status_update)" },
  { value: "rider_status_update", label: "Rider Status Update (rider_status_update)" },
  { value: "store_status_update", label: "Store Status Update (store_status_update)" },
  { value: "inventory_update", label: "Inventory Update (inventory_update)" },
  { value: "item_state_toggle", label: "Item State Toggle (item_state_toggle)" },
  { value: "menu_sync", label: "Menu Sync (menu_sync)" },
  { value: "__custom__", label: "Custom Event Type..." },
];

export const URBAN_PIPER_RETRIAL_UNIT_OPTIONS = [
  { value: "minutes", label: "Minutes" },
  { value: "hours", label: "Hours" },
  { value: "seconds", label: "Seconds" },
];

export const URBAN_PIPER_DEFAULT_BULK_EVENTS = [
  "order_placed",
  "order_status_update",
  "rider_status_update",
  "store_status_update",
  "inventory_update",
] as const;

export const URBAN_PIPER_STATUS_FILTER_KEYS = {
  ALL: "ALL",
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE",
} as const;

export type UrbanPiperStatusFilter =
  (typeof URBAN_PIPER_STATUS_FILTER_KEYS)[keyof typeof URBAN_PIPER_STATUS_FILTER_KEYS];

export const URBAN_PIPER_PAGE_SIZE_OPTIONS = [10, 20, 50] as const;
export const URBAN_PIPER_DEFAULT_PAGE_SIZE = 20;
export const URBAN_PIPER_DEFAULT_EVENT_TYPE = "order_placed";
export const URBAN_PIPER_CUSTOM_EVENT_VALUE = "__custom__";
export const URBAN_PIPER_DEFAULT_RETRIAL_UNIT = "minutes";
export const URBAN_PIPER_COPY_TIMEOUT_MS = 1800;

export const formatEventTypeLabel = (val?: string): string => {
  if (!val) return "";
  const existing = URBAN_PIPER_EVENT_TYPE_OPTIONS.find((opt) => opt.value === val);
  if (existing) return existing.label;

  const title = val
    .replace(/[_-]/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return `${title} (${val})`;
};

export const parseWebhooksResponse = (res: any): UrbanPiperWebhook[] => {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.webhooks)) return res.data.webhooks;
  if (Array.isArray(res?.webhooks)) return res.webhooks;
  if (Array.isArray(res?.data?.records)) return res.data.records;
  if (Array.isArray(res?.records)) return res.records;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  if (
    res?.data &&
    typeof res.data === "object" &&
    (res.data.id || res.data.webhook_id || res.data.event_type)
  ) {
    return [res.data];
  }
  if (typeof res === "object" && (res.id || res.webhook_id || res.event_type)) {
    return [res];
  }
  return [];
};

export const parseDefaultWebhookEvents = (
  res: any
): { options: { value: string; label: string }[]; bulkEvents: string[] } => {
  let list: any[] = [];
  if (Array.isArray(res)) {
    list = res;
  } else if (Array.isArray(res?.data)) {
    list = res.data;
  } else if (Array.isArray(res?.data?.events)) {
    list = res.data.events;
  } else if (Array.isArray(res?.data?.defaultEvents)) {
    list = res.data.defaultEvents;
  } else if (Array.isArray(res?.data?.records)) {
    list = res.data.records;
  } else if (Array.isArray(res?.events)) {
    list = res.events;
  } else if (Array.isArray(res?.records)) {
    list = res.records;
  } else if (Array.isArray(res?.data?.data)) {
    list = res.data.data;
  }

  if (!list || list.length === 0) {
    return {
      options: [...URBAN_PIPER_EVENT_TYPE_OPTIONS],
      bulkEvents: [...URBAN_PIPER_DEFAULT_BULK_EVENTS],
    };
  }

  const rawValues: string[] = [];
  const options: { value: string; label: string }[] = [];

  list.forEach((item: any) => {
    let val = "";
    let lbl = "";
    if (typeof item === "string") {
      val = item.trim();
      lbl = formatEventTypeLabel(val);
    } else if (item && typeof item === "object") {
      val = (
        item.value ||
        item.event_type ||
        item.event ||
        item.name ||
        item.id ||
        ""
      )
        .toString()
        .trim();
      lbl =
        item.label ||
        item.title ||
        item.description ||
        item.name ||
        formatEventTypeLabel(val);
    }
    if (val && val !== URBAN_PIPER_CUSTOM_EVENT_VALUE && !rawValues.includes(val)) {
      rawValues.push(val);
      options.push({ value: val, label: lbl });
    }
  });

  if (options.length === 0) {
    return {
      options: [...URBAN_PIPER_EVENT_TYPE_OPTIONS],
      bulkEvents: [...URBAN_PIPER_DEFAULT_BULK_EVENTS],
    };
  }

  // Always ensure custom option is present at the end
  options.push({
    value: URBAN_PIPER_CUSTOM_EVENT_VALUE,
    label: "Custom Event Type...",
  });

  return {
    options,
    bulkEvents:
      rawValues.length > 0
        ? rawValues
        : [...URBAN_PIPER_DEFAULT_BULK_EVENTS],
  };
};

